import type { Hotel } from '@elara/client-core/models/HotelModel';

import { cn } from '@elara/client-core/lib/utils';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './accordion.js';
import { Logo } from './logo.js';
import { IconSizes, MaterialIcon } from './MaterialIcon.js';

/** Why the benefits exist, and their one condition: said the same way wherever they are listed. */
const PARTNER_NOTE = 'We’ve partnered with Serandipians to bring you additional benefits at this hotel.';
const AVAILABILITY_NOTE = 'Benefits are subject to availability.';

/**
 * The hotel guide's Elara Benefits: the Serandipians benefits that apply at an affiliated hotel. The room guide's
 * panel ({@link ElaraBenefitsList}) held open: the Elara mark and heading, why the benefits exist and their
 * condition, then the benefits with accent checks.
 */
export function ElaraBenefitsBanner({ hotel, className }: { hotel: Hotel; className?: string }) {
  if (!hotel.serandipiansBenefits || hotel.serandipiansBenefits.length === 0) return null;

  return (
    <section className={cn('flex flex-col gap-3 px-4 pt-3 pb-4 text-sm bg-background-secondary rounded-md', className)}>
      <h3 className="flex items-center gap-2 m-0 font-sans text-sm font-medium">
        <Logo variant="symbol" className="w-5 h-5" />
        <span>Elara Benefits available</span>
      </h3>
      <p className="text-pretty">
        {PARTNER_NOTE} {AVAILABILITY_NOTE}
      </p>
      <BenefitChecklist benefits={hotel.serandipiansBenefits} />
    </section>
  );
}

/** The benefits, each with an accent check: the same list in the hotel banner and the room panel. */
function BenefitChecklist({ benefits }: { benefits: string[] }) {
  return (
    <ul className="grid grid-cols-1 gap-1">
      {benefits.map((benefit, index) => (
        <li key={`${benefit}-${index}`} className="flex items-start gap-2">
          <MaterialIcon icon="check" size={IconSizes.sm} className="text-accent" />
          <span className="text-pretty">{benefit.trim()}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The room's Elara Benefits above its description, as a section that opens: "Elara Benefits included" with the
 * Elara mark, which expands to why they exist and their condition, then the benefits with accent checks. Closed
 * at first, so it doesn't push the rates down.
 */
export function ElaraBenefitsList({
  benefits,
  defaultOpen = false,
}: {
  benefits: string[];
  /** Open from the start (Storybook shows it both ways); the room guide leaves it closed. */
  defaultOpen?: boolean;
}) {
  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={defaultOpen ? 'elara-benefits' : undefined}
      className="text-sm bg-background-secondary rounded-md"
    >
      <AccordionItem value="elara-benefits" className="border-b-0">
        <AccordionTrigger className="group mx-0 px-4 py-3 rounded-md font-medium hover:text-current">
          <span className="flex flex-1 items-center gap-2">
            <Logo variant="symbol" className="w-5 h-5" />
            <span>Elara Benefits included</span>
          </span>
          {/* Beside the chevron while closed; fades once the section is open and its details are showing. */}
          <span className="mr-2 text-xs font-normal text-muted-foreground transition-opacity group-data-[state=open]:opacity-0">
            See details
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <div className="flex flex-col gap-3 px-4 pb-4">
            <p className="text-sm text-pretty">
              {PARTNER_NOTE} {AVAILABILITY_NOTE}
            </p>
            <BenefitChecklist benefits={benefits} />
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
